import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutGrid,
  Building2,
  Users,
  IdCard,
  GraduationCap,
  Stethoscope,
  HardHat,
  ShieldCheck,
  UtensilsCrossed,
  Waves,
  ClipboardCheck,
  ClipboardList,
  CalendarDays,
  Bell,
  FileBarChart2,
  FileSpreadsheet,
  Layers,
  UserCog,
  History as HistoryIcon,
  Send,
  GitMerge,
  ChevronDown,
  X,
  Download as DownloadIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';

const consultantMenu = [
  { to: '/panoramica', label: 'PANORAMICA CLIENTE', icon: IdCard },
  { to: '/aziende', label: 'ELENCO AZIENDE', icon: Building2 },
  { to: '/lavoratori', label: 'LAVORATORI', icon: Users },
  { to: '/sicurezza', label: 'SICUREZZA SUL LAVORO', icon: ShieldCheck },
  { to: '/sorveglianza', label: 'SORVEGLIANZA SANITARIA', icon: Stethoscope },
  { to: '/formazione', label: 'FORMAZIONE', icon: GraduationCap },
  { to: '/haccp', label: 'HACCP', icon: UtensilsCrossed },
  { to: '/registri-haccp', label: 'REGISTRI HACCP', icon: ClipboardList },
  { to: '/piscina', label: 'PISCINA', icon: Waves },
  { to: '/sopralluoghi', label: 'SOPRALLUOGHI', icon: ClipboardCheck },
  { to: '/registri', label: 'REGISTRI', icon: ClipboardList },
  { to: '/dpi', label: 'DPI', icon: HardHat },
  { to: '/calendario', label: 'CALENDARIO', icon: CalendarDays },
  { to: '/comunicazioni', label: 'COMUNICAZIONI', icon: Bell },
  { to: '/report', label: 'REPORT', icon: FileBarChart2 },
  { to: '/operazioni-massive', label: 'OPERAZIONI MASSIVE', icon: Layers },
  { to: '/mailing', label: 'MAILING', icon: Send },
  { to: '/utenti', label: 'GESTIONE UTENTI', icon: UserCog },
  { to: '/audit', label: 'AUDIT LOG', icon: HistoryIcon },
  { to: '/migrazione', label: 'MIGRAZIONE DATI', icon: DownloadIcon },
  { to: '/riconciliazione', label: 'RICONCILIAZIONE', icon: GitMerge },
  { to: '/riconciliazione/duplicati-privati', label: 'DUPLICATI PRIVATI', icon: Users },
  { to: '/riconciliazione/merge-da-file', label: 'UNISCI DA FILE', icon: GitMerge },
  { to: '/riconciliazione/ruoli-import', label: 'IMPORT RUOLI EXCEL', icon: FileSpreadsheet },
  { to: '/riconciliazione/formazione-align', label: 'IMPORT FORMAZIONE EXCEL', icon: FileSpreadsheet },
  { to: '/riconciliazione/visite-align', label: 'IMPORT VISITE EXCEL', icon: FileSpreadsheet },
  { to: '/riconciliazione/presenze-import', label: 'AGGIORNA PRESENZE', icon: UserCog },
  { to: '/riconciliazione/contratti-servizi', label: 'IMPORT CONTRATTI/SERVIZI', icon: FileSpreadsheet },
  { to: '/riconciliazione/normalizza-nomi', label: 'NORMALIZZA NOMI (CF)', icon: UserCog },
];

const clientMenu = [
  { to: '/panoramica', label: 'PANORAMICA', icon: LayoutGrid },
  { to: '/lavoratori', label: 'LAVORATORI', icon: Users },
  { to: '/sicurezza', label: 'SICUREZZA SUL LAVORO', icon: ShieldCheck },
  { to: '/sorveglianza', label: 'SORVEGLIANZA SANITARIA', icon: Stethoscope },
  { to: '/formazione', label: 'FORMAZIONE', icon: GraduationCap },
  { to: '/haccp', label: 'HACCP', icon: UtensilsCrossed },
  { to: '/registri-haccp', label: 'REGISTRI HACCP', icon: ClipboardList },
  { to: '/piscina', label: 'PISCINA', icon: Waves },
  { to: '/sopralluoghi', label: 'SOPRALLUOGHI', icon: ClipboardCheck },
  { to: '/registri', label: 'REGISTRI', icon: ClipboardList },
  { to: '/dpi', label: 'DPI', icon: HardHat },
  { to: '/calendario', label: 'CALENDARIO', icon: CalendarDays },
  { to: '/comunicazioni', label: 'COMUNICAZIONI', icon: Bell },
  { to: '/report', label: 'REPORT', icon: FileBarChart2 },
  { to: '/operazioni-massive', label: 'OPERAZIONI MASSIVE', icon: Layers },
];

const Sidebar = ({ collapsed, mobileOpen, onCloseMobile }) => {
  const { user } = useAuth();
  const baseMenu = user?.role === 'client' ? clientMenu : consultantMenu;
  // Optional fine-grained menu permissions (admin-configured).
  // If user.menuAccess is empty/missing → all items visible (back-compat).
  // Otherwise show only items whose 'to' path's first segment is in the allow-list.
  const allowed = Array.isArray(user?.menuAccess) ? user.menuAccess.map(String) : [];
  const menu = allowed.length === 0 ? baseMenu : baseMenu.filter((i) => {
    const seg = (i.to || '').split('/').filter(Boolean)[0] || '';
    return allowed.includes(seg);
  });
  const [groupOpen, setGroupOpen] = useState(true);

  // On mobile, fully visible drawer; on desktop, collapsed/expanded depending on flag
  // Build classes:
  // - Mobile (<lg): translate based on mobileOpen
  // - Desktop (lg): always visible, width based on collapsed
  const widthClass = collapsed ? 'lg:w-16' : 'lg:w-64';
  const translateClass = mobileOpen
    ? 'translate-x-0'
    : '-translate-x-full lg:translate-x-0';

  return (
    <aside
      className={`fixed left-0 top-0 z-40 h-screen bg-[#023F80] text-gray-200 transition-all duration-300 w-64 ${widthClass} ${translateClass} flex flex-col`}
    >
      {/* Logo header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-white/5 shrink-0">
        {!collapsed ? (
          <Logo variant="light" size="md" />
        ) : (
          <div className="mx-auto hidden lg:block">
            <Logo variant="light" size="md" showWordmark={false} />
          </div>
        )}
        {/* Close button visible on mobile only */}
        <button
          onClick={onCloseMobile}
          className="lg:hidden p-1 rounded-md hover:bg-white/10"
          aria-label="Chiudi menu"
        >
          <X size={18} />
        </button>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3">
        {user?.role !== 'client' && !collapsed && (
          <button
            onClick={() => setGroupOpen(!groupOpen)}
            className="w-full px-4 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 hover:text-gray-200"
          >
            <span>Consulente</span>
            <ChevronDown
              size={14}
              className={`transition-transform ${groupOpen ? '' : '-rotate-90'}`}
            />
          </button>
        )}
        {groupOpen && (
          <ul className="space-y-0.5 px-2">
            {menu.map((m) => {
              const Icon = m.icon;
              return (
                <li key={m.to}>
                  <NavLink
                    to={m.to}
                    onClick={onCloseMobile}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-[12.5px] font-semibold tracking-wide transition-colors ${
                        isActive
                          ? 'bg-white text-[#023F80] shadow-sm'
                          : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && <span className="truncate">{m.label}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="px-4 py-3 border-t border-white/5 text-[10px] text-gray-500">
          © 2026 SafetyDesk81 • v1.0
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
